// pages/ComplaintsPage.jsx
import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore"
import { db } from "../../firebase/config"
import dayjs from "dayjs"
import PageHeader from "../../components/Elements/PageHeader"
import { ExclamationCircleOutlined, RollbackOutlined } from "@ant-design/icons"
import { useAuthContext } from "../../hooks/useAuthContext"
import ComplaintReprintForm from "./ComplaintReprintForm"
import { updateProductOrdersArray } from "../../utils/updateProductOrdersArray"

const { Text } = Typography
const { RangePicker } = DatePicker

// --- helpers -------------------------------------------------
function toDayjs(tsOrDate) {
  if (!tsOrDate) return null
  if (tsOrDate.seconds != null) return dayjs.unix(tsOrDate.seconds)
  return dayjs(tsOrDate)
}
function fmt(ts) {
  const d = toDayjs(ts)
  return d && d.isValid() ? d.format("YYYY-MM-DD HH:mm") : "-"
}
function num(n) {
  const v = Number(n)
  return Number.isFinite(v) ? v : 0
}
function computeEstimatedLoss(orderSnapshot) {
  if (!orderSnapshot) return 0
  const qty = num(orderSnapshot.amountCreated)
  const pricePerThousand = num(orderSnapshot.price)
  return (qty * pricePerThousand) / 1000
}
// ----------------------------------------------------------------

export default function Complaints() {
  // default range = this month (Dayjs)
  const [range, setRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")])
  const [allComplaints, setAllComplaints] = useState([])
  const [respFilter, setRespFilter] = useState([])
  const [mgrFilter, setMgrFilter] = useState([])

  const [open, setOpen] = useState(false)
  const [activeRow, setActiveRow] = useState(null)

  const [returningId, setReturningId] = useState(null)
  const [loading, setLoading] = useState(false)

  const { user, admin, semiAdmin, devSpecialist } = useAuthContext()
  const isAdmin = admin || semiAdmin || devSpecialist

  // helper to check date range on the client
  function inRange(createdAt, startDate, endDate) {
    if (!createdAt) return false
    const d = toDayjs(createdAt)
    if (!d?.isValid?.()) return false
    return d.toDate() >= startDate && d.toDate() <= endDate
  }

  async function handleApprove(complaint) {
    try {
      const complaintId = complaint?.id
      const orderIdFromSnapshot = complaint?.orderSnapshot?.id
      const orderIdFallback = complaint?.orderId
      const orderId = orderIdFromSnapshot || orderIdFallback

      if (!orderId) {
        message.error("Order ID not found on complaint.")
        return
      }

      setReturningId(complaintId)

      const batch = writeBatch(db)
      const orderRef = doc(db, "orders", orderId)
      const complaintRef = doc(db, "complaints", complaintId)

      // Move order to 996 and mark complaint approved
      batch.update(orderRef, {
        orderStatus: 996,
        complaintStatus: "Approved",
      })

      batch.update(complaintRef, {
        status: "Approved",
        approvedAt: serverTimestamp(),
        approvedByUid: user?.uid || null,
        approvedByEmail: user?.email || null,
      })

      await batch.commit()

      const productId = complaint?.orderSnapshot?.selectedProduct?.id
      const orderNumber = complaint?.orderNumber
      const clientId = complaint?.orderSnapshot?.clientId

      if (productId && orderNumber) {
        await updateProductOrdersArray("remove", complaint.orderSnapshot)
      }

      // Refund credit to client since order is being removed
      if (clientId) {
        try {
          const clientRef = doc(db, "clients", clientId)
          const clientSnap = await getDoc(clientRef)
          if (clientSnap.exists()) {
            const clientData = clientSnap.data()
            const orderPrice = num(complaint?.orderSnapshot?.price) || 0
            const orderQty = num(complaint?.orderSnapshot?.amountCreated) || 0
            const orderCost = orderQty > 0 ? (orderQty * orderPrice) / 1000 : 0

            const currentCreditUsed = num(clientData.creditUsed)
            const currentCreditLeft = num(clientData.creditLeft)

            // Batch update to refund credit
            const creditBatch = writeBatch(db)
            creditBatch.update(clientRef, {
              creditUsed: Math.max(0, currentCreditUsed - orderCost),
              creditLeft: currentCreditLeft + orderCost,
            })
            await creditBatch.commit()
          }
        } catch (creditErr) {
          console.warn("Could not refund credit for approved complaint:", creditErr)
        }
      }

      message.success("Complaint approved. Order moved to 996 and credit refunded.")
      if (activeRow?.id === complaintId) setOpen(false)
    } catch (err) {
      console.error(err)
      message.error("Failed to approve complaint.")
    } finally {
      setReturningId(null)
    }
  }

  async function handleDeny(complaint) {
    try {
      const complaintId = complaint?.id
      const orderIdFromSnapshot = complaint?.orderSnapshot?.id
      const orderIdFallback = complaint?.orderId
      const orderId = orderIdFromSnapshot || orderIdFallback

      if (!orderId) {
        message.error("Order ID not found on complaint.")
        return
      }

      setReturningId(complaintId)

      const batch = writeBatch(db)
      const orderRef = doc(db, "orders", orderId)
      const complaintRef = doc(db, "complaints", complaintId)

      // Remove the review flag from order
      batch.update(orderRef, {
        complaintStatus: null,
      })

      // Delete complaint
      batch.delete(complaintRef)

      await batch.commit()

      message.success("Complaint denied. Review removed.")
      if (activeRow?.id === complaintId) setOpen(false)
    } catch (err) {
      console.error(err)
      message.error("Failed to deny complaint.")
    } finally {
      setReturningId(null)
    }
  }

  async function handleReturn(complaint) {
    try {
      const complaintId = complaint?.id
      const orderIdFromSnapshot = complaint?.orderSnapshot?.id
      const orderIdFallback = complaint?.orderId
      const orderId = orderIdFromSnapshot || orderIdFallback

      if (!orderId) {
        message.error("Order ID not found on complaint.")
        return
      }

      setReturningId(complaintId)

      // (Optional) sanity-check the order exists before batching
      const orderRef = doc(db, "orders", orderId)
      const orderSnap = await getDoc(orderRef)
      if (!orderSnap.exists()) {
        setReturningId(null)
        message.error("Order document not found.")
        return
      }

      const batch = writeBatch(db)
      // 1) update order to status 6
      batch.update(orderRef, { orderStatus: 6 })

      // 2) delete this complaint
      const complaintRef = doc(db, "complaints", complaintId)
      batch.delete(complaintRef)

      await batch.commit()

      message.success("Order returned and complaint deleted.")
      // Drawer would auto close if its record disappears from the list,
      // but we can explicitly close if it's the one currently open
      if (activeRow?.id === complaintId) setOpen(false)
    } catch (err) {
      console.error(err)
      message.error("Failed to return order. Please try again.")
    } finally {
      setReturningId(null)
    }
  }

  // live Firestore query by role
  useEffect(() => {
    const [start, end] = range || []
    if (!start || !end || !user) return

    setLoading(true)

    const startDate = start.startOf("day").toDate()
    const endDate = end.endOf("day").toDate()

    const colRef = collection(db, "complaints")

    // Preferred queries (require composite index for non-admin case)
    const preferredQuery = isAdmin
      ? query(
          colRef,
          where("createdAt", ">=", startDate),
          where("createdAt", "<=", endDate),
          orderBy("createdAt", "desc")
        )
      : query(
          colRef,
          where("createdByUid", "==", user.uid),
          where("createdAt", ">=", startDate),
          where("createdAt", "<=", endDate),
          orderBy("createdAt", "desc")
        )

    // Subscribe with automatic fallback if index missing
    const unsub = onSnapshot(
      preferredQuery,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setAllComplaints(rows)
        setLoading(false)
      },
      (err) => {
        // If a composite index is missing, gracefully fallback
        if (err?.code === "failed-precondition" && !isAdmin) {
          // Fallback: no composite index required.
          const fbRef = query(colRef, where("createdByUid", "==", user.uid))
          const unsubFb = onSnapshot(
            fbRef,
            (snap2) => {
              // Client-side filter by date + sort by createdAt desc
              const rows = snap2.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((c) => inRange(c.createdAt, startDate, endDate))
                .sort(
                  (a, b) =>
                    (toDayjs(b.createdAt)?.valueOf?.() || 0) -
                    (toDayjs(a.createdAt)?.valueOf?.() || 0)
                )
              setAllComplaints(rows)
              setLoading(false)
            },
            (err2) => {
              console.error("Fallback complaints fetch error:", err2)
              setLoading(false)
              message.error("Unable to fetch complaints.")
            }
          )
          // return the fallback unsub for cleanup
          return () => unsubFb()
        } else {
          console.error("Complaints fetch error:", err)
          setLoading(false)
          message.error("Unable to fetch complaints.")
        }
      }
    )

    return () => unsub()
  }, [range, user?.uid, isAdmin, user])

  const responsibilityOptions = useMemo(() => {
    const set = new Set()
    allComplaints.forEach((c) => c.responsibility && set.add(c.responsibility))
    return [...set].sort().map((v) => ({ value: v, label: v }))
  }, [allComplaints])

  const managerOptions = useMemo(() => {
    const set = new Set()
    allComplaints.forEach((c) => set.add(c.createdByEmail || c.createdByUid || "unknown"))
    return [...set].sort().map((v) => ({ value: v, label: v }))
  }, [allComplaints])

  const filtered = useMemo(() => {
    return allComplaints.filter((c) => {
      const respOk = respFilter.length === 0 || respFilter.includes(c.responsibility)
      const mgr = c.createdByEmail || c.createdByUid || "unknown"
      const mgrOk = mgrFilter.length === 0 || mgrFilter.includes(mgr)
      return respOk && mgrOk
    })
  }, [allComplaints, respFilter, mgrFilter])

  const totals = useMemo(() => {
    const byResp = {}
    let grand = 0
    filtered.forEach((c) => {
      const est = computeEstimatedLoss(c.orderSnapshot)
      grand += est
      const key = c.responsibility || "Other"
      byResp[key] = (byResp[key] || 0) + est
    })
    return { grand, byResp }
  }, [filtered])

  const columns = [
    // { title: "ID", dataIndex: "humanId", width: 110, render: (v) => <Tag>{v || "-"}</Tag> },
    {
      title: "Order",
      dataIndex: "orderNumber",
      width: 140,
      render: (v, r) => v || r.orderId || "-",
    },
    {
      title: "Client",
      dataIndex: ["orderSnapshot", "client", "client"],
      ellipsis: true,
      width: 120,
      render: (v) => (
        <Tooltip placement="topLeft" title={v}>
          <Text style={{ color: "#13c2c2" }}>{v || "-"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["orderSnapshot", "selectedProduct", "product", "productName"],
      ellipsis: true,
      width: 120,
      render: (v) => (
        <Tooltip placement="topLeft" title={v}>
          <Text style={{ color: "#fa8c16" }}>{v || "-"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Amount",
      dataIndex: ["orderSnapshot", "amountCreated"],
      ellipsis: true,
      width: 100,
      render: (v) => (
        <Text italic style={{ color: "green" }}>
          {v || "-"}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 80,
      render: (v) => <Tag color="geekblue">{v || "-"}</Tag>,
    },
    {
      title: "Responsibility",
      dataIndex: "responsibility",
      width: 120,
      render: (v) => <Tag color="volcano">{v || "-"}</Tag>,
    },
    {
      title: "Manager",
      dataIndex: "createdByEmail",
      width: 220,
      hidden: !isAdmin,
      render: (v, r) => v || r.createdByUid || "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 170,
      render: (v) => fmt(v),
      sorter: (a, b) => (a?.createdAt?.seconds || 0) - (b?.createdAt?.seconds || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Action",
      width: 260,
      render: (val, rec) => (
        <Space onClick={(e) => e.stopPropagation()}>
          {isAdmin && rec.status === "In review" && (
            <>
              <Popconfirm
                title="Approve this complaint?"
                okText="Approve"
                cancelText="Cancel"
                onConfirm={(e) => {
                  e?.stopPropagation?.()
                  handleApprove(rec)
                }}
                onCancel={(e) => e?.stopPropagation?.()}>
                <Button type="primary" size="small" loading={returningId === rec.id}>
                  Approve
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Deny this complaint?"
                description="This will delete the complaint and remove review flag from the order."
                okText="Deny"
                cancelText="Cancel"
                onConfirm={(e) => {
                  e?.stopPropagation?.()
                  handleDeny(rec)
                }}
                onCancel={(e) => e?.stopPropagation?.()}>
                <Button danger size="small" loading={returningId === rec.id}>
                  Deny
                </Button>
              </Popconfirm>
            </>
          )}

          {isAdmin && rec.status === "Approved" && (
            <Popconfirm
              title="Return this order?"
              description="This will send the order back to warehouse and delete complaint."
              okText="Yes, return"
              cancelText="Cancel"
              onConfirm={(e) => {
                e?.stopPropagation?.()
                handleReturn(rec)
              }}
              onCancel={(e) => e?.stopPropagation?.()}>
              <Button icon={<RollbackOutlined />} size="small" loading={returningId === rec.id}>
                Return
              </Button>
            </Popconfirm>
          )}

          <ComplaintReprintForm complaint={rec} />
        </Space>
      ),
    },

    {
      title: "Estimated Loss",
      key: "loss",
      align: "right",
      width: 160,
      render: (_, r) => <Text strong>{computeEstimatedLoss(r.orderSnapshot).toFixed(2)} €</Text>,
    },
    {
      title: "Comment",
      dataIndex: "comment",
      ellipsis: true,

      render: (v) => (
        <Tooltip placement="topLeft" title={v}>
          <Text>{v || "-"}</Text>
        </Tooltip>
      ),
    },
  ]

  const onRowClick = (record) => {
    setActiveRow(record)
    setOpen(true)
  }

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Complaints" icon={<ExclamationCircleOutlined />} />
        </Col>
        <Col span={24}>
          <Card style={{ marginTop: 12 }}>
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} md={10} lg={8}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text type="secondary">Date Range</Text>
                  <RangePicker
                    allowClear={false}
                    value={range} // Dayjs[]
                    onChange={(vals) => {
                      if (!vals || vals.length !== 2) return
                      setRange(vals) // keep as Dayjs; convert only for query
                    }}
                    style={{ width: "100%" }}
                  />
                </Space>
              </Col>

              <Col xs={24} md={7} lg={6}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text type="secondary">Responsibility</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All"
                    value={respFilter}
                    onChange={setRespFilter}
                    options={responsibilityOptions}
                    style={{ width: "100%" }}
                    maxTagCount="responsive"
                  />
                </Space>
              </Col>

              <Col xs={24} md={7} lg={6}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text type="secondary">Manager</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All"
                    value={mgrFilter}
                    onChange={setMgrFilter}
                    options={managerOptions}
                    style={{ width: "100%" }}
                    maxTagCount="responsive"
                  />
                </Space>
              </Col>

              <Col xs={24} md={24} lg={4}>
                <Row gutter={12}>
                  <Col span={12}>
                    <Statistic title="Complaints" value={filtered.length} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Total Loss (€)" value={totals.grand.toFixed(2)} />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Divider style={{ marginTop: 16, marginBottom: 12 }} />

            <Row gutter={[12, 12]}>
              {Object.keys(totals.byResp).length === 0 ? (
                <Col span={24}>
                  <Empty description="No breakdown yet" />
                </Col>
              ) : (
                Object.entries(totals.byResp).map(([resp, val]) => (
                  <Col key={resp} xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Card size="small">
                      <Space align="center">
                        <Badge color="volcano" />
                        <Text strong>{resp}</Text>
                      </Space>
                      <div style={{ marginTop: 6 }}>
                        <Text type="secondary">Loss</Text>
                        <div>
                          <Text strong>{val.toFixed(2)} €</Text>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))
              )}
            </Row>
          </Card>
        </Col>
        <Col span={24}>
          <Card>
            <Table
              size="small"
              rowKey={(r) => r.id}
              columns={columns}
              dataSource={filtered}
              loading={loading}
              onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: "pointer" },
              })}
              pagination={{ pageSize: 100, showSizeChanger: true }}
            />
          </Card>

          <Drawer
            title={
              activeRow
                ? `${activeRow.humanId || ""} — ${activeRow.orderNumber || activeRow.orderId || ""}`
                : "Complaint"
            }
            width={720}
            open={open}
            onClose={() => setOpen(false)}>
            {!activeRow ? (
              <Empty />
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                {(() => {
                  const o = activeRow.orderSnapshot || {}
                  const amountCreated = num(o.amountCreated)
                  const boxesCount = num(o.boxesCount ?? o.boxCount)
                  const pricePerThousand = num(o.price)
                  const loss = computeEstimatedLoss(o)

                  const clientName = o?.client?.client ?? "-"
                  const productName = o?.selectedProduct?.product?.productName ?? "-"
                  const machine = o?.selectedProduct?.machine ?? "-"
                  const createdTs = activeRow?.createdAt
                  const dueTs = o?.orderDueDate

                  return (
                    <>
                      {/* Top stats */}
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <Card size="small">
                            <Statistic title="Estimated Loss (€)" value={loss.toFixed(2)} />
                          </Card>
                        </Col>
                        <Col xs={24} md={12}>
                          <Card size="small">
                            <Statistic title="Created" value={fmt(createdTs)} />
                          </Card>
                        </Col>
                      </Row>

                      {/* Complaint meta */}
                      <Card size="small" title="Complaint">
                        <Row gutter={[16, 8]}>
                          <Col xs={24} md={12}>
                            <Text type="secondary">Type</Text>
                            <div>
                              <Tag color="geekblue">{activeRow.type || "-"}</Tag>
                            </div>
                          </Col>
                          <Col xs={24} md={12}>
                            <Text type="secondary">Responsibility</Text>
                            <div>
                              <Tag color="volcano">{activeRow.responsibility || "-"}</Tag>
                            </div>
                          </Col>
                        </Row>
                        <Divider />
                        <Text type="secondary">Comment</Text>
                        <div style={{ whiteSpace: "pre-wrap" }}>{activeRow.comment || "-"}</div>
                      </Card>

                      {/* Order summary */}
                      <Card size="small" title="Order Summary">
                        <Descriptions column={2} bordered size="small">
                          <Descriptions.Item label="Order">
                            {activeRow.orderNumber || activeRow.orderId || "-"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Manager">
                            {activeRow.createdByEmail || activeRow.createdByUid || "-"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Client">{clientName}</Descriptions.Item>
                          <Descriptions.Item label="Product">{productName}</Descriptions.Item>
                          <Descriptions.Item label="Machine">{machine}</Descriptions.Item>
                          <Descriptions.Item label="Due Date">{fmt(dueTs)}</Descriptions.Item>
                          <Descriptions.Item label="Boxes">{boxesCount}</Descriptions.Item>
                          <Descriptions.Item label="Amount Created">
                            {amountCreated}
                          </Descriptions.Item>
                          <Descriptions.Item label="Price (€/1000)">
                            {pricePerThousand}
                          </Descriptions.Item>
                          <Descriptions.Item label="Est. Loss (€)">
                            <Text strong>{loss.toFixed(2)}</Text>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </>
                  )
                })()}
              </Space>
            )}
          </Drawer>
        </Col>
      </Row>
    </div>
  )
}
